import { HeaderImage } from "../../../../resources/modelTypes";

/**
 * Resolves a browser-loadable URL for a header photo placement.
 *
 * Prefer preview/original: the read serializer already expands a cropped
 * override into absolute storage URLs on those fields. `image` is the raw
 * storage path (e.g. "cropped/….jpg") and is not safe to use as img src.
 */
export const resolveHeaderImageUrl = (
  headerImage: HeaderImage,
): string | null => headerImage.preview || headerImage.original || null;

/** Header photos in display order (ascending position). */
export const getOrderedHeaderImages = (
  headerImages: HeaderImage[],
): HeaderImage[] =>
  [...headerImages].sort((left, right) => left.position - right.position);

/** Header photo at a fixed modular slot (position), if any. */
export const getHeaderImageAtPosition = (
  headerImages: HeaderImage[],
  position: number,
): HeaderImage | undefined =>
  headerImages.find((headerImage) => headerImage.position === position);

export type HeaderMediaFrame = {
  position: number;
  url: string;
};

/** Header photos as display frames, preserving each placement's position. */
export const getHeaderMediaFrames = (
  headerImages: HeaderImage[],
): HeaderMediaFrame[] =>
  getOrderedHeaderImages(headerImages).flatMap((headerImage) => {
    const url = resolveHeaderImageUrl(headerImage);

    return url ? [{ position: headerImage.position, url }] : [];
  });

/**
 * Modular slot index for the primary visible header photo (lowest position),
 * or 0 when the gallery is empty.
 */
export const getPrimaryHeaderSlotPosition = (
  headerImages: HeaderImage[],
): number => {
  const ordered = getOrderedHeaderImages(headerImages);

  return ordered.length > 0 ? ordered[0].position : 0;
};

/**
 * Resolves the URL for the primary (first-by-position) header photo.
 */
export const getPrimaryHeaderImageUrl = (
  headerImages: HeaderImage[],
): string | null => {
  const ordered = getOrderedHeaderImages(headerImages);

  if (ordered.length === 0) {
    return null;
  }

  return resolveHeaderImageUrl(ordered[0]);
};
